import { Body, VButton, VDialog, VTextarea } from '@vrzl/ui';
import { useState } from 'react';
import { useLocation } from 'react-router';

import { useSubmitExtractionFeedback } from '../api/mutations';

const ERROR_MESSAGE = 'De feedback kon niet verstuurd worden. Probeer het opnieuw.';

type ExtractionFeedbackModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  caseId: string;
  fieldName: string;
  criterionId: string;
  criterionName: string;
  /** Called once after a successful submit; the parent marks this criterion as sent. */
  onSent: () => void;
};

export function ExtractionFeedbackModal({
  open,
  onOpenChange,
  caseId,
  fieldName,
  criterionId,
  criterionName,
  onSent,
}: ExtractionFeedbackModalProps) {
  const location = useLocation();
  const [feedback, setFeedback] = useState<boolean | null>(null);
  const [correction, setCorrection] = useState('');
  const { mutate, isPending, isError, reset } = useSubmitExtractionFeedback();

  const handleOpenChange = (nextOpen: boolean) => {
    if (isPending) return;
    if (!nextOpen) {
      setFeedback(null);
      setCorrection('');
      reset();
    }
    onOpenChange(nextOpen);
  };

  const trimmedCorrection = correction.trim();
  const canSubmit = feedback !== null && trimmedCorrection !== '' && !isPending;

  const submit = () => {
    if (feedback === null || !trimmedCorrection) return;
    mutate(
      {
        caseId,
        fieldName,
        criterionId,
        feedback,
        route: location.pathname + location.search,
        correction: trimmedCorrection,
      },
      {
        onSuccess: () => {
          onSent();
          handleOpenChange(false);
        },
      },
    );
  };

  return (
    <VDialog
      open={open}
      onOpenChange={handleOpenChange}
      size="sm"
      title={`Feedback op "${criterionName}"`}
      footer={
        <div className="flex justify-end gap-2">
          <VButton variant="ghost" onClick={() => handleOpenChange(false)} disabled={isPending}>
            Annuleren
          </VButton>
          <VButton onClick={submit} disabled={!canSubmit}>
            {isPending ? 'Versturen…' : 'Versturen'}
          </VButton>
        </div>
      }
    >
      <div className="space-y-4 px-4 py-3">
        <div className="flex gap-2">
          <VButton
            variant={feedback === true ? 'primary' : 'outline'}
            size="sm"
            aria-pressed={feedback === true}
            onClick={() => setFeedback(true)}
            disabled={isPending}
          >
            Positief
          </VButton>
          <VButton
            variant={feedback === false ? 'primary' : 'outline'}
            size="sm"
            aria-pressed={feedback === false}
            onClick={() => setFeedback(false)}
            disabled={isPending}
          >
            Negatief
          </VButton>
        </div>
        <VTextarea
          label="Toelichting"
          placeholder="Beschrijf wat er beter kan of wat de juiste waarde is…"
          value={correction}
          onChange={(e) => setCorrection(e.target.value)}
          disabled={isPending}
        />
        {isError && (
          <Body as="p" tone="error">
            {ERROR_MESSAGE}
          </Body>
        )}
      </div>
    </VDialog>
  );
}
